/**
 * Utilities for drawing compact waveform strips in the diagnostics player.
 */

export type PeaksFromBufferOptions = {
  /**
   * Exact bucket count (e.g. unit tests). When omitted, buckets are chosen
   * from buffer length so long files still get fine-grained peaks, capped
   * by `maxBars`.
   */
  barCount?: number;
  /** Upper cap on bucket count for decode + draw cost (default 3200). */
  maxBars?: number;
  /** At least this many PCM samples per bucket when auto-sizing (default 16). */
  minSamplesPerBucket?: number;
};

/** Deterministic pseudo-peaks for HLS (no full decode) — stable per seed string. */
export function syntheticWaveformPeaks(seed: string, barCount: number): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const peaks: number[] = [];
  for (let i = 0; i < barCount; i++) {
    hash = (hash * 1103515245 + 12345 + i) | 0;
    const n = (Math.abs(hash) % 1000) / 1000;
    // Keep a floor so the strip never reads as a flat line.
    peaks.push(0.12 + n * 0.88);
  }
  return peaks;
}

/** Mono down-mix (average) for waveform analysis — preserves signed samples. */
function mergeChannelsSignedMono(buffer: AudioBuffer): Float32Array {
  const len = buffer.length;
  const out = new Float32Array(len);
  const chCount = buffer.numberOfChannels;
  if (chCount === 0) {
    return out;
  }
  if (chCount === 1) {
    out.set(buffer.getChannelData(0));
    return out;
  }
  const ch0 = buffer.getChannelData(0);
  const ch1 = buffer.getChannelData(1);
  for (let i = 0; i < len; i++) {
    out[i] = ((ch0[i] ?? 0) + (ch1[i] ?? 0)) * 0.5;
  }
  return out;
}

function autoBarCount(buffer: AudioBuffer, options?: PeaksFromBufferOptions): number {
  const maxBars = options?.maxBars ?? 3200;
  const minSpb = options?.minSamplesPerBucket ?? 16;
  const len = buffer.length;
  if (len <= 0) {
    return 1;
  }
  // Enough buckets that wide UIs get detail; cap work + memory.
  let bars = Math.ceil(len / minSpb);
  bars = Math.min(maxBars, Math.max(64, bars));
  return bars;
}

/**
 * Downsample signed mono PCM to one height per bar: blend of peak magnitude
 * and RMS so quiet passages still show texture, not a solid block.
 * Returns values in ~[0, 1] after normalizing, with a slight gamma lift so
 * low-level detail reads on screen.
 */
export function peaksFromAudioBuffer(
  buffer: AudioBuffer,
  options?: PeaksFromBufferOptions,
): number[] {
  const barCount =
    options?.barCount != null && options.barCount > 0
      ? options.barCount
      : autoBarCount(buffer, options);

  const mono = mergeChannelsSignedMono(buffer);
  if (mono.length === 0) {
    return new Array(barCount).fill(0);
  }
  const samplesPerBar = Math.max(1, Math.ceil(mono.length / barCount));
  const peaks: number[] = [];
  let globalMax = 1e-9;

  for (let b = 0; b < barCount; b++) {
    const start = b * samplesPerBar;
    const end = Math.min(mono.length, start + samplesPerBar);
    let maxAbs = 0;
    let sumSq = 0;
    let count = 0;
    for (let i = start; i < end; i++) {
      const s = mono[i] ?? 0;
      const a = Math.abs(s);
      if (a > maxAbs) {
        maxAbs = a;
      }
      sumSq += s * s;
      count++;
    }
    const rms = count > 0 ? Math.sqrt(sumSq / count) : 0;
    // Peak carries transients; RMS keeps body in sparse / percussive material.
    const combined = 0.58 * maxAbs + 0.42 * rms;
    peaks.push(combined);
    if (combined > globalMax) {
      globalMax = combined;
    }
  }

  const inv = 1 / globalMax;
  const gamma = 0.72;
  return peaks.map((p) => Math.min(1, (p * inv) ** gamma));
}

/**
 * Fetch URL as audio and decode to normalized peak envelope. Uses a throwaway
 * AudioContext (decode only — not routed to speakers).
 */
export async function decodeUrlToWaveformPeaks(
  url: string,
  signal?: AbortSignal,
  peakOptions?: PeaksFromBufferOptions,
): Promise<number[] | null> {
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', signal });
    if (!response.ok) {
      return null;
    }
    const arrayBuffer = await response.arrayBuffer();
    if (signal?.aborted) {
      return null;
    }
    const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      return null;
    }
    const ctx = new AudioContextCtor();
    try {
      const copy = arrayBuffer.slice(0);
      const audioBuffer = await ctx.decodeAudioData(copy);
      if (signal?.aborted) {
        return null;
      }
      return peaksFromAudioBuffer(audioBuffer, peakOptions);
    } finally {
      await ctx.close().catch(() => {});
    }
  } catch {
    return null;
  }
}
